import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from './ui/button';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { useAuth } from '../context/AuthContext';
import { useUnread } from '../context/UnreadContext';
import { NAV_DESTINATIONS } from '../config/navItems';
import {
  Home, Hash, Ship, Calendar, Users, Trophy, Search, Bell, MessageSquare,
  Command, ChevronDown, User, Palette, BarChart2, Settings, BarChart3, Inbox, LogOut,
  Route as RouteIcon,
} from 'lucide-react';

const PRIMARY_TABS = [
  { icon: Home, label: 'Accueil', path: '/' },
  { icon: Hash, label: 'Channels', path: '/channels' },
  { icon: Ship, label: 'Mon Odyssée', path: '/progression' },
  { icon: Calendar, label: 'Événements', path: '/calendar' },
  { icon: Users, label: 'Membres', path: '/members-map' },
  { icon: Trophy, label: 'Classement', path: '/leaderboard' },
];

const PRIMARY_PATHS = PRIMARY_TABS.map((t) => t.path);

/** Barre de navigation globale (ordinateur). Sur mobile, c'est MobileNav qui prend le relais. */
const TopBar = ({ onSearchClick }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAdmin, logout } = useAuth();
  const { total: channelsUnread, dmTotal } = useUnread();
  const [moreOpen, setMoreOpen] = useState(false);

  const isActive = (path) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  const secondary = NAV_DESTINATIONS.filter((d) => !PRIMARY_PATHS.includes(d.path));
  const initials = `${user?.first_name?.[0] || ''}${user?.last_name?.[0] || ''}`.toUpperCase() || 'EO';

  return (
    <header
      className="hidden md:block sticky top-0 z-30 border-b border-border bg-card/90 backdrop-blur-xl"
      data-testid="global-top-bar"
    >
      <div className="max-w-[1800px] mx-auto px-4 h-14 flex items-center gap-4">
        {/* Marque */}
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 shrink-0"
          data-testid="topbar-logo"
        >
          <span
            className="w-9 h-9 rounded-full bg-white flex items-center justify-center overflow-hidden p-1"
            style={{ border: '1.5px solid hsl(var(--gold))' }}
          >
            <img src="/logo-192.png" alt="Odys" className="w-full h-full object-contain" />
          </span>
          <span className="font-heading font-bold text-sm whitespace-nowrap hidden lg:block">
            L&apos;Équipage <span className="text-primary">Odys</span>
          </span>
        </button>

        {/* Onglets principaux */}
        <nav className="flex items-center gap-0.5 min-w-0 overflow-x-auto">
          {PRIMARY_TABS.map((tab) => {
            const Icon = tab.icon;
            const active = isActive(tab.path);
            return (
              <button
                key={tab.path}
                onClick={() => navigate(tab.path)}
                className={`relative h-14 px-3 flex items-center gap-2 text-sm whitespace-nowrap transition-colors ${
                  active ? 'text-primary font-medium' : 'text-muted-foreground hover:text-foreground'
                }`}
                data-testid={`topbar-tab-${tab.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <Icon className="w-4 h-4" />
                <span className="hidden xl:inline">{tab.label}</span>
                {tab.path === '/channels' && channelsUnread > 0 && (
                  <span
                    className="min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center"
                    style={{ background: 'hsl(var(--red-dossier))', color: '#fff' }}
                  >
                    {channelsUnread > 99 ? '99+' : channelsUnread}
                  </span>
                )}
                {active && (
                  <motion.span
                    layoutId="topbar-indicator"
                    className="absolute bottom-0 left-2 right-2 h-[2px] bg-primary rounded-t-full"
                    transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                  />
                )}
              </button>
            );
          })}

          {/* Toutes les autres destinations */}
          <DropdownMenu open={moreOpen} onOpenChange={setMoreOpen}>
            <DropdownMenuTrigger asChild>
              <button
                className="h-14 px-3 flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground whitespace-nowrap"
                data-testid="topbar-more"
              >
                Plus
                <ChevronDown className="w-4 h-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-64 max-h-[70vh] overflow-y-auto">
              {secondary.map((item) => {
                const Icon = item.icon;
                return (
                  <DropdownMenuItem
                    key={item.path}
                    onClick={() => navigate(item.path)}
                    data-testid={`topbar-more-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <Icon className={`w-4 h-4 mr-2 ${item.color}`} />
                    {item.label}
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        </nav>

        {/* Actions personnelles */}
        <div className="ml-auto flex items-center gap-1 shrink-0">
          <Button
            variant="ghost"
            size="sm"
            onClick={onSearchClick}
            className="hidden lg:flex items-center gap-2 text-muted-foreground"
            data-testid="topbar-search"
          >
            <Search className="w-4 h-4" />
            <span className="text-sm">Rechercher</span>
            <kbd className="ml-1 px-1.5 py-0.5 bg-muted rounded text-[10px] flex items-center gap-0.5">
              <Command className="w-3 h-3" />K
            </kbd>
          </Button>
          <Button variant="ghost" size="icon" onClick={onSearchClick} className="lg:hidden" aria-label="Rechercher">
            <Search className="w-5 h-5" />
          </Button>

          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/dm')}
            className="relative"
            data-testid="topbar-dm"
            title="Messages privés"
          >
            <MessageSquare className="w-5 h-5" />
            {dmTotal > 0 && (
              <span
                className="absolute top-1 right-1 min-w-[16px] h-[16px] px-1 rounded-full text-[9px] font-bold flex items-center justify-center"
                style={{ background: 'hsl(var(--red-dossier))', color: '#fff' }}
              >
                {dmTotal > 99 ? '99+' : dmTotal}
              </span>
            )}
          </Button>

          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/notifications')}
            data-testid="topbar-notifications"
            title="Notifications"
          >
            <Bell className="w-5 h-5" />
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="ml-1 rounded-full" data-testid="topbar-avatar">
                <Avatar className="w-9 h-9 border border-border">
                  <AvatarImage src={user?.avatar_url} />
                  <AvatarFallback className="text-xs">{initials}</AvatarFallback>
                </Avatar>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem onClick={() => navigate('/profile')} data-testid="topbar-menu-profile">
                <User className="w-4 h-4 mr-2" />
                Mon profil
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/frise')} data-testid="topbar-menu-frise">
                <RouteIcon className="w-4 h-4 mr-2" />
                Ma frise de voyage
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/cabines')} data-testid="topbar-menu-cabines">
                <Palette className="w-4 h-4 mr-2" />
                Ma cabine
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/my-stats')}>
                <BarChart2 className="w-4 h-4 mr-2" />
                Mes stats
              </DropdownMenuItem>
              {isAdmin && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/passerelle')} data-testid="topbar-menu-passerelle">
                    <Inbox className="w-4 h-4 mr-2" />
                    Passerelle du Capitaine
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/admin')}>
                    <Settings className="w-4 h-4 mr-2" />
                    Administration
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/analytics')}>
                    <BarChart3 className="w-4 h-4 mr-2" />
                    Analytics
                  </DropdownMenuItem>
                </>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={logout} className="text-red-600" data-testid="topbar-menu-logout">
                <LogOut className="w-4 h-4 mr-2" />
                Se déconnecter
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
};

export default TopBar;
